import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plug, Save } from "lucide-react";

const PROVIDERS = [
    { key: "openai_api_key", name: "OpenAI", desc: "GPT image / Whisper / API automations." },
    { key: "google_flow_key", name: "Google Flow", desc: "Cinematic AI video generation." },
    { key: "higgsfield_key", name: "Higgsfield / Hixfield", desc: "Camera-driven AI video." },
    { key: "runway_key", name: "Runway", desc: "Gen-3 video generation." },
    { key: "kling_key", name: "Kling", desc: "Long-form AI video." },
    { key: "canva_key", name: "Canva API", desc: "Push slides into Canva." },
    { key: "meta_key", name: "Meta / IG / FB", desc: "Schedule & publish." },
    { key: "youtube_key", name: "YouTube API", desc: "Upload & publish." },
    { key: "cloud_storage_key", name: "Cloud Storage", desc: "S3 / GCS / R2 buckets." },
];

export default function Integrations() {
    const [status, setStatus] = useState({});
    const [draft, setDraft] = useState({});

    useEffect(() => { api.get("/integrations").then(({data}) => setStatus(data)); }, []);

    const save = async () => {
        const payload = {};
        Object.entries(draft).forEach(([k, v]) => { if (v) payload[k] = v; });
        if (Object.keys(payload).length === 0) { toast.message("Nothing to save."); return; }
        await api.put("/integrations", payload);
        const { data } = await api.get("/integrations");
        setStatus(data); setDraft({});
        toast.success("Integrations updated.");
    };

    return (
        <div className="p-6 sm:p-8 lg:p-10 max-w-5xl mx-auto" data-testid="integrations-page">
            <div className="mb-8">
                <div className="label-micro mb-2">v1 Placeholders</div>
                <h1 className="font-display text-4xl font-medium tracking-tight">Future API Integrations</h1>
                <p className="text-zinc-500 text-sm mt-2 max-w-2xl">
                    Drop-in slots for the generation tools you already use. v1 stores keys for later activation —
                    no auto-generation runs in this build.
                </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {PROVIDERS.map(p => (
                    <div key={p.key} className="card-base p-5">
                        <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-3">
                                <div className="w-9 h-9 bg-zinc-900 grid place-items-center rounded-sm border border-zinc-800">
                                    <Plug className="w-4 h-4 text-zinc-400" />
                                </div>
                                <div>
                                    <div className="font-medium">{p.name}</div>
                                    <div className="text-xs text-zinc-500">{p.desc}</div>
                                </div>
                            </div>
                            <span className={`text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 border rounded-sm ${status[p.key] ? "border-emerald-500/30 text-emerald-400 bg-emerald-500/10" : "border-zinc-700 text-zinc-500"}`}>
                                {status[p.key] ? "CONFIGURED" : "NOT SET"}
                            </span>
                        </div>
                        <input
                            data-testid={`int-${p.key}`}
                            type="password"
                            placeholder={status[p.key] ? "•••••••• (saved — paste new to overwrite)" : `${p.name} API key`}
                            value={draft[p.key] || ""}
                            onChange={e => setDraft(d => ({ ...d, [p.key]: e.target.value }))}
                            className="w-full bg-black border border-zinc-800 rounded-sm px-3 py-2 font-mono text-xs focus:border-zinc-500 outline-none"
                        />
                    </div>
                ))}
            </div>

            <div className="flex justify-end mt-6">
                <button onClick={save} data-testid="int-save" className="btn-primary"><Save className="w-4 h-4" /> Save keys</button>
            </div>
        </div>
    );
}
